import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Calendar, DollarSign, Users, Clock, Briefcase, Building2, Eye, Send, AlertCircle, FileText, Download, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Opportunities = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showProposalsModal, setShowProposalsModal] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchOpportunities();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/opportunities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpportunities(response.data.opportunities || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError('Failed to load opportunities');
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowDetailsModal(true);
  };

  const handleApply = (opportunity) => {
    alert(`Apply to: ${opportunity.program_title}\nThis will open the proposal submission form.`);
    // TODO: Implement proposal submission
  };

  const handleViewProposals = async (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowProposalsModal(true);
    setLoadingProposals(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/opportunities/${opportunity.id}/proposals/received`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProposals(response.data.proposals || []);
    } catch (err) {
      console.error('Error fetching proposals:', err);
      setProposals([]);
    } finally {
      setLoadingProposals(false);
    }
  };

  const handleDownloadProposal = (proposal) => {
    // Create a text file with proposal details
    const proposalText = `
PROPOSAL DETAILS
================

Proposal Number: ${proposal.proposal_number || 'N/A'}
Trainer: ${proposal.trainer_details?.name || proposal.full_name || 'N/A'}
Email: ${proposal.trainer_details?.email || proposal.email || 'N/A'}
Status: ${proposal.status || 'N/A'}

PROGRAM TITLE
${proposal.proposal_title || proposal.title || 'N/A'}

TRAINING FORMAT
${proposal.training_format || 'N/A'}

TARGET AUDIENCE
${proposal.target_audience || 'N/A'}

LEARNING OBJECTIVES
${proposal.learning_objectives || 'N/A'}

METHODOLOGY
${proposal.methodology || 'N/A'}

PROPOSED TIMELINE
${proposal.proposed_timeline || 'N/A'}

KEY DELIVERABLES
${proposal.key_deliverables || 'N/A'}

PRICING BREAKDOWN
${proposal.pricing_breakdown || proposal.amount || 'N/A'}

TRAINER QUALIFICATIONS
Experience: ${proposal.years_experience || 'N/A'}
Certifications: ${proposal.certifications || 'N/A'}
Specializations: ${proposal.specializations || 'N/A'}

ADDITIONAL INFORMATION
${proposal.additional_info || proposal.description || 'N/A'}

Submitted: ${proposal.submitted_at ? new Date(proposal.submitted_at).toLocaleString() : new Date(proposal.created_at).toLocaleString()}
    `.trim();

    // Create and download file
    const blob = new Blob([proposalText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Proposal_${proposal.proposal_number || proposal.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleViewProposalDetails = (proposal) => {
    navigate(`/proposal/${proposal.id}`);
  };

  const handleMessageTrainer = (proposal) => {
    navigate(`/messages/compose?to=${proposal.trainer_id}&proposalId=${proposal.id}`);
  };

  const handleAcceptProposal = async (proposal) => {
    if (!window.confirm(`Are you sure you want to accept the proposal from ${proposal.trainer_details?.name || proposal.full_name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/proposals/${proposal.id}/status?status=accepted`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Proposal accepted successfully!');
      // Refresh proposals
      handleViewProposals(selectedOpportunity);
    } catch (err) {
      console.error('Error accepting proposal:', err);
      alert('Failed to accept proposal');
    }
  };

  const handleRejectProposal = (proposal) => {
    const rejectionMessage = `Dear ${proposal.trainer_details?.name || proposal.full_name},

Thank you for submitting your proposal for "${proposal.proposal_title || 'our training opportunity'}".

After careful consideration, we regret to inform you that we will not be moving forward with your proposal at this time.

We appreciate the time and effort you put into your submission.

Best regards,
[Your Organization]`;

    navigate(`/messages/compose?to=${proposal.trainer_id}&proposalId=${proposal.id}&action=reject&message=${encodeURIComponent(rejectionMessage)}`);
  };

  const filteredOpportunities = opportunities.filter(opp =>
    opp.program_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opp.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opp.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading training opportunities...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-900 font-semibold mb-2">Failed to load opportunities</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={fetchOpportunities} className="bg-blue-600 hover:bg-blue-700">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Training Opportunities</h1>
          <p className="text-gray-600 mt-1">Browse and apply to training requirements posted by organizations</p>
        </div>
        <Badge variant="secondary" className="text-lg px-4 py-2">
          {filteredOpportunities.length} Active
        </Badge>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search by title, location, or organization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Opportunities List */}
      {filteredOpportunities.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">No opportunities found</p>
            <p className="text-gray-500 text-sm">Check back later for new training requirements</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOpportunities.map((opportunity) => (
            <Card key={opportunity.id} className="hover:shadow-lg transition-shadow duration-200">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {opportunity.program_title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 mr-1" />
                        {opportunity.organization_name}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {opportunity.location}
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">
                    Active
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center text-sm">
                    <Clock className="w-4 h-4 mr-2 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Duration</p>
                      <p className="font-medium">{opportunity.duration}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-sm">
                    <DollarSign className="w-4 h-4 mr-2 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Budget</p>
                      <p className="font-medium">{opportunity.budget}</p>
                    </div>
                  </div>

                  {opportunity.start_date && (
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Start Date</p>
                        <p className="font-medium">{new Date(opportunity.start_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}

                  {opportunity.participant_count && (
                    <div className="flex items-center text-sm">
                      <Users className="w-4 h-4 mr-2 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Participants</p>
                        <p className="font-medium">{opportunity.participant_count}</p>
                      </div>
                    </div>
                  )}
                </div>

                {opportunity.objectives && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {opportunity.objectives}
                  </p>
                )}

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className="flex items-center">
                      <Eye className="w-3 h-3 mr-1" />
                      {opportunity.views || 0} views
                    </span>
                    <span>Posted {new Date(opportunity.created_at).toLocaleDateString()}</span>
                    {currentUser?.user_type === 'organization' && (
                      <button
                        onClick={() => handleViewProposals(opportunity)}
                        className="flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors cursor-pointer px-3 py-1 bg-blue-50 rounded-lg"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Total Proposals Rcvd: <span className="ml-1 text-lg">{opportunity.proposals_count || 0}</span>
                      </button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(opportunity)}
                    >
                      View Details
                    </Button>
                    {currentUser?.user_type !== 'organization' && (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleApply(opportunity)}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Apply Now
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOpportunity?.program_title}</DialogTitle>
          </DialogHeader>

          {selectedOpportunity && (
            <div className="space-y-6 py-4">
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 mr-2 text-gray-500" />
                  <span>{selectedOpportunity.organization_name}</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                  <span>{selectedOpportunity.location}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Duration</p>
                  <p className="font-semibold">{selectedOpportunity.duration}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Budget</p>
                  <p className="font-semibold">{selectedOpportunity.budget}</p>
                </div>
                {selectedOpportunity.start_date && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Start Date</p>
                    <p className="font-semibold">{new Date(selectedOpportunity.start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedOpportunity.training_mode && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Mode</p>
                    <p className="font-semibold">{selectedOpportunity.training_mode}</p>
                  </div>
                )}
              </div>

              {selectedOpportunity.objectives && (
                <div>
                  <h4 className="font-semibold mb-2">Program Objectives</h4>
                  <p className="text-gray-600 text-sm">{selectedOpportunity.objectives}</p>
                </div>
              )}

              {selectedOpportunity.target_audience && (
                <div>
                  <h4 className="font-semibold mb-2">Target Audience</h4>
                  <p className="text-gray-600 text-sm">{selectedOpportunity.target_audience}</p>
                </div>
              )}

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Contact Information</h4>
                <p className="text-sm text-gray-600">
                  <strong>Contact Person:</strong> {selectedOpportunity.contact_person}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong> {selectedOpportunity.contact_email}
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
                {currentUser?.user_type !== 'organization' && (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleApply(selectedOpportunity)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Apply Now
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Proposals Received Modal - Table View */}
      <Dialog open={showProposalsModal} onOpenChange={setShowProposalsModal}>
        <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Proposals Received - {selectedOpportunity?.program_title}
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                Total: {proposals.length}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {loadingProposals ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : proposals.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No proposals received yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b-2 border-gray-200">
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">S.No</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Trainer / Training Firm</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Date of Submission</th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-700">Proposal</th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-700">Accept/Reject</th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-700">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposals.map((proposal, index) => (
                      <tr key={proposal.id} className="border-b hover:bg-gray-50 transition-colors">
                        {/* S.No */}
                        <td className="p-3 text-center font-medium text-gray-700">
                          {index + 1}
                        </td>

                        {/* Trainer / Training Firm - Clickable */}
                        <td className="p-3">
                          <button
                            onClick={() => navigate(`/digitpro/${proposal.trainer_id}`)}
                            className="flex items-center space-x-3 hover:bg-blue-50 p-2 rounded-lg transition-colors cursor-pointer w-full"
                          >
                            {proposal.trainer_details?.avatar ? (
                              <img 
                                src={proposal.trainer_details.avatar} 
                                alt={proposal.trainer_details.name}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                                {(proposal.trainer_details?.name || proposal.full_name || 'T')[0].toUpperCase()}
                              </div>
                            )}
                            <div className="text-left">
                              <p className="font-semibold text-blue-600 hover:text-blue-800">
                                {proposal.trainer_details?.name || proposal.full_name || 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-600">
                                {proposal.company || 'Independent Trainer'}
                              </p>
                            </div>
                          </button>
                        </td>

                        {/* Date of Submission */}
                        <td className="p-3 text-sm text-gray-700">
                          {proposal.submitted_at 
                            ? new Date(proposal.submitted_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : new Date(proposal.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                          }
                        </td>

                        {/* Proposal - Clickable link to view & download */}
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/proposal/${proposal.id}`)}
                              className="flex items-center space-x-1"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadProposal(proposal)}
                              className="flex items-center space-x-1"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>

                        {/* Accept/Reject */}
                        <td className="p-3">
                          <div className="flex items-center justify-center space-x-2">
                            {proposal.status === 'accepted' ? (
                              <Badge className="bg-green-100 text-green-800 px-3 py-1">Accepted</Badge>
                            ) : proposal.status === 'rejected' ? (
                              <Badge className="bg-red-100 text-red-800 px-3 py-1">Rejected</Badge>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleAcceptProposal(proposal)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectProposal(proposal)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </td>

                        {/* Message */}
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMessageTrainer(proposal)}
                            className="flex items-center space-x-1 mx-auto"
                          >
                            <Send className="w-4 h-4" />
                            <span>Message</span>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowProposalsModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Opportunities;
